/**
 * Modèle de données d'une facture : création, normalisation, migration, dates.
 *
 * Module pur. Le modèle est volontairement plat et sérialisable tel quel en
 * JSON (localStorage, export/import).
 */

import {normaliserMontant} from './calculs'
import {EMETTEUR_FEDERATION, IBAN_FEDERATION} from './federation'

export const VERSION_MODELE = 1

// Bleu AdminLTE : la facture reprend la couleur de l'interface, sans choix à faire
export const COULEUR_ACCENT = '#3c8dbc'

let compteurIdentifiants = 0

function identifiant (prefixe) {
  compteurIdentifiants += 1
  return prefixe + '_' + Date.now() + '_' + compteurIdentifiants
}

function texte (valeur) {
  return valeur === null || valeur === undefined ? '' : String(valeur)
}

export function ligneVierge () {
  return {
    id: identifiant('l'),
    description: '',
    quantite: 1,
    prixUnitaire: 0,
    tauxTva: 0
  }
}

/**
 * L'émetteur est toujours la fédération : ses coordonnées ne se saisissent pas,
 * elles sont reprises d'EMETTEUR_FEDERATION et rétablies à chaque relecture.
 * `logoFederation` demande le logo officiel ; `logoDataUrl` ne sert qu'à un
 * logo téléversé à la main.
 */
function emetteurVierge () {
  return Object.assign({}, EMETTEUR_FEDERATION, {
    logoDataUrl: null,
    logoFederation: true
  })
}

function clientVierge () {
  return {
    source: 'libre',
    idSource: null,
    nom: '',
    adresse: '',
    cp: '',
    ville: '',
    pays: 'Belgique',
    email: '',
    numEntreprise: ''
  }
}

/**
 * La TVA figure d'office sur la facture : son taux doit se lire explicitement,
 * même à 0 % pour une ASBL exonérée. Elle reste retirable au cas par cas.
 */
function optionsVierges () {
  return {
    tva: true,
    remise: false,
    frais: false,
    notes: false,
    conditions: true,
    reference: false,
    echeance: true,
    logo: true,
    qr: true,
    paiement: true
  }
}

export function aujourdhuiFr () {
  return dateVersFr(new Date())
}

export const DELAI_ECHEANCE_JOURS = 30

export function factureVierge (parametres) {
  const options = parametres || {}
  const dateEmission = options.dateEmission || aujourdhuiFr()
  const emetteur = emetteurVierge()

  return {
    version: VERSION_MODELE,
    id: identifiant('f'),
    numero: options.numero || '',
    dateEmission: dateEmission,
    dateEcheance: dateFrPlusJours(dateEmission, DELAI_ECHEANCE_JOURS),
    reference: '',
    template: options.template || 'moderne',
    modele: options.modele || null,
    origine: options.origine || null,
    // Renseignés par le serveur une fois la facture persistée
    idServeur: options.idServeur || null,
    statut: options.statut || null,
    devise: 'EUR',
    emetteur: emetteur,
    client: clientVierge(),
    lignes: [ligneVierge()],
    options: optionsVierges(),
    tva: {mode: 'global', taux: 0, mentionExoneration: ''},
    remise: {type: 'pourcentage', valeur: 0},
    frais: 0,
    notes: '',
    conditionsPaiement: '',
    paiement: {
      beneficiaire: emetteur.nom,
      iban: IBAN_FEDERATION,
      bic: '',
      communicationStructuree: ''
    }
  }
}

function normaliserLigne (ligne) {
  const source = ligne || {}
  return {
    id: source.id || identifiant('l'),
    description: texte(source.description),
    quantite: normaliserMontant(source.quantite),
    prixUnitaire: normaliserMontant(source.prixUnitaire),
    tauxTva: normaliserMontant(source.tauxTva)
  }
}

/**
 * Remplit les champs manquants et convertit les montants saisis à la main
 * (« 12,50 ») en nombres. Appelée à chaque relecture du localStorage et avant
 * tout calcul ou export.
 */
export function normaliser (facture) {
  const source = facture || {}
  const vierge = factureVierge()
  const lignes = Array.isArray(source.lignes) && source.lignes.length
    ? source.lignes.map(normaliserLigne)
    : [ligneVierge()]

  return {
    version: VERSION_MODELE,
    id: source.id || vierge.id,
    numero: texte(source.numero),
    dateEmission: source.dateEmission || vierge.dateEmission,
    dateEcheance: source.dateEcheance || null,
    reference: texte(source.reference),
    template: source.template || 'moderne',
    modele: source.modele || null,
    origine: source.origine || null,
    idServeur: source.idServeur || null,
    statut: source.statut || null,
    devise: source.devise || 'EUR',
    emetteur: Object.assign(emetteurVierge(), {
      logoDataUrl: (source.emetteur && source.emetteur.logoDataUrl) || null,
      logoFederation: !(source.emetteur && source.emetteur.logoFederation === false)
    }),
    client: Object.assign(clientVierge(), source.client || {}),
    lignes: lignes,
    options: Object.assign(optionsVierges(), source.options || {}),
    tva: {
      mode: (source.tva && source.tva.mode) === 'ligne' ? 'ligne' : 'global',
      taux: normaliserMontant(source.tva && source.tva.taux),
      mentionExoneration: texte(source.tva && source.tva.mentionExoneration)
    },
    remise: {
      type: (source.remise && source.remise.type) === 'montant' ? 'montant' : 'pourcentage',
      valeur: normaliserMontant(source.remise && source.remise.valeur)
    },
    frais: normaliserMontant(source.frais),
    notes: texte(source.notes),
    conditionsPaiement: texte(source.conditionsPaiement),
    paiement: Object.assign(
      {beneficiaire: '', iban: '', bic: '', communicationStructuree: ''},
      source.paiement || {}
    )
  }
}

/**
 * Montée de version d'une facture relue du localStorage. Aujourd'hui une seule
 * version existe : normaliser suffit. Les migrations futures s'ajoutent ici,
 * en cascade sur `version`.
 */
export function migrer (facture) {
  return normaliser(facture)
}

function dateVersFr (date) {
  const jour = ('0' + date.getDate()).slice(-2)
  const mois = ('0' + (date.getMonth() + 1)).slice(-2)
  return jour + '/' + mois + '/' + date.getFullYear()
}

/**
 * Validation stricte du format jj/mm/aaaa, repris de GenerationCourrier.vue :
 * un mois au-delà de 12 produirait silencieusement une autre date.
 */
export function dateFrValide (valeur) {
  const correspondance = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valeur || '')
  if (!correspondance) return false
  const jour = parseInt(correspondance[1], 10)
  const mois = parseInt(correspondance[2], 10)
  const annee = parseInt(correspondance[3], 10)
  const date = new Date(annee, mois - 1, jour)
  return date.getDate() === jour && date.getMonth() === mois - 1 && date.getFullYear() === annee
}

export function frVersDate (valeur) {
  if (!dateFrValide(valeur)) return null
  const morceaux = valeur.split('/')
  return new Date(parseInt(morceaux[2], 10), parseInt(morceaux[1], 10) - 1, parseInt(morceaux[0], 10))
}

/**
 * « FACT-2026-0001 » s'affiche « FACTURE 2026-0001 » : le mot figure déjà dans
 * l'intitulé, le répéter donnerait « FACTURE FACT-... ».
 */
export function intituleFacture (numero) {
  const propre = String(numero || '').trim().replace(/^FACT[-_\s]*/i, '')
  return ('FACTURE ' + propre).trim()
}

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
]

/**
 * « 07/10/2026 » devient « 7 octobre 2026 ».
 * Écrit à la main plutôt qu'avec moment : la locale française de moment est
 * posée au démarrage de l'application, pas dans un module isolé ni en test.
 */
export function dateFrLongue (valeur) {
  const date = frVersDate(valeur)
  if (!date) return ''
  return date.getDate() + ' ' + MOIS[date.getMonth()] + ' ' + date.getFullYear()
}

export function dateFrPlusJours (valeur, jours) {
  const date = frVersDate(valeur)
  if (!date) return null
  date.setDate(date.getDate() + (parseInt(jours, 10) || 0))
  return dateVersFr(date)
}

export default {
  VERSION_MODELE,
  DELAI_ECHEANCE_JOURS,
  COULEUR_ACCENT,
  ligneVierge,
  factureVierge,
  normaliser,
  migrer,
  aujourdhuiFr,
  dateFrValide,
  frVersDate,
  dateFrPlusJours,
  dateFrLongue,
  intituleFacture
}



// WEBPACK FOOTER //
// ./src/utils/facture/modele.js